'use client';
import type { ComponentProps } from 'react';
import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Condition } from '@/utils/react';
import type { Currency } from '@/constants/currency/currency';
import { convert } from '@/utils/function';
import { formatCurrency } from '@/utils/domain';

type ProductInfoTextProps = ComponentProps<typeof Text>;

export const ProductInformationBrand = ({ children, ...rest }: ProductInfoTextProps) => {
  return (
    <Text
      color="neutral.90"
      textStyle="body3"
      fontWeight="bold"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      wordBreak="break-all"
      lineClamp={1}
      {...rest}
    >
      {children}
    </Text>
  );
};

export const ProductInformationName = ({ children, ...rest }: ProductInfoTextProps) => {
  return (
    <Text
      color="neutral.60"
      textStyle="body3"
      fontWeight="regular"
      overflow="hidden"
      textOverflow="ellipsis"
      lineClamp={2}
      {...rest}
    >
      {children}
    </Text>
  );
};

type ProductInformationPriceProps = {
  salePrice?: number;
  originalPrice: number;
  currency: Currency;
} & ComponentProps<typeof Flex>;

export const ProductInformationPrice = ({
  currency,
  salePrice,
  originalPrice,
  ...rest
}: ProductInformationPriceProps) => {
  return (
    <Condition
      expression={Boolean(salePrice)}
      then={
        <Flex gap={2} alignItems="center" {...rest}>
          <Text color="neutral.90" textStyle="body1" fontWeight="medium">
            {convert(salePrice, (salePrice) => formatCurrency(currency, salePrice.toFixed(2)))}
          </Text>

          <Text color="neutral.30" textStyle="body3" fontWeight="regular" textDecoration="line-through">
            {convert(originalPrice, (originalPrice) => formatCurrency(currency, originalPrice.toFixed(2)))}
          </Text>
        </Flex>
      }
      else={
        <Text color="neutral.90" textStyle="body1" fontWeight="medium">
          {formatCurrency(currency, originalPrice?.toFixed(2))}
        </Text>
      }
    />
  );
};

export const ProductInformation = {
  Brand: ProductInformationBrand,
  Name: ProductInformationName,
  Price: ProductInformationPrice,
};
